import React, { useEffect, useState, useCallback } from 'react';
import { Search, Filter, Plus, X, Trash2, AlertTriangle, Pencil } from 'lucide-react';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { useCnpjLookup } from '../../hooks/useCnpjLookup';
import { useCepLookup } from '../../hooks/useCepLookup';
import CrecheCadastroFields, { CrecheCadastroData, CRECHE_CADASTRO_VAZIO } from '../../shared/CrecheCadastroFields';

interface UsuarioResumo {
  id: number;
  nome?: string;
  email?: string;
  cpf?: string;
  rg_numero?: string;
  rg_uf?: string;
  tipo_usuario?: string;
}

interface CrecheResumo {
  id: number;
  nome: string;
  nome_fantasia?: string;
  cnpj?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade: string;
  estado: string;
  cep?: string;
  telefone?: string;
  email?: string;
  diretor?: string;
  representante_legal_nome?: string;
  representante_legal_cpf?: string;
  representante_legal_rg?: string;
  representante_legal_rg_uf?: string;
  representante_legal_nacionalidade?: string;
  representante_legal_estado_civil?: string;
  representante_legal_profissao?: string;
  status: 'pendente' | 'ativa' | 'suspensa' | 'negada';
  onboarding_status: 'LEAD' | 'PAGAMENTO_APROVADO' | 'TRIAL' | 'TENANT_PROVISIONADO' | 'ATIVO' | 'STANDBY';
  criado_em?: string;
  plano?: string;
  usuarios?: UsuarioResumo[];
}

interface FiltrosAvancados {
  status: string;
  plano: string;
  onboarding_status: string;
}

export default function GestaoTenantsPage() {
  const { success, error: showError, warning } = useNotification();
  const [creches, setCreches] = useState<CrecheResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [motivoNegacao, setMotivoNegacao] = useState('');
  const [dialogId, setDialogId] = useState<number | null>(null);
  const [abrirModalFiltros, setAbrirModalFiltros] = useState(false);

  // Modal criar / editar (compartilhado)
  const [abrirModal, setAbrirModal] = useState(false);
  const [modoEditar, setModoEditar] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState<CrecheCadastroData>(CRECHE_CADASTRO_VAZIO);

  // Exclusão
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteConfirmNome, setDeleteConfirmNome] = useState('');
  const [excluindo, setExcluindo] = useState(false);

  // CNPJ / CEP lookup
  const { buscar: buscarCnpj, buscando: buscandoCNPJ, erro: erroCNPJ, setErro: setErroCNPJ } = useCnpjLookup();
  const { buscar: buscarCep, buscando: buscandoCEP } = useCepLookup();

  const [filtros, setFiltros] = useState<FiltrosAvancados>({
    status: '',
    plano: '',
    onboarding_status: '',
  });

  // ─── Dados ─────────────────────────────────────────────────────────────────

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const response = await api.get<CrecheResumo[]>('/creches');
      setCreches(response.data ?? []);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // ─── Ações ─────────────────────────────────────────────────────────────────

  async function atualizarStatus(id: number, status: CrecheResumo['status'], motivo?: string) {
    try {
      await api.patch(`/creches/${id}/status`, { status, motivo_negacao: motivo ?? null });
      await carregarDados();
    } finally {
      setDialogId(null);
      setMotivoNegacao('');
    }
  }

  async function excluirTenant(id: number) {
    setExcluindo(true);
    try {
      await api.delete(`/creches/${id}`);
      success(`Tenant "${deleteConfirmNome}" removido com sucesso!`);
      setDeleteConfirmId(null);
      setDeleteConfirmNome('');
      await carregarDados();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erro ao excluir tenant';
      showError(msg);
    } finally {
      setExcluindo(false);
    }
  }

  // ─── Abrir modal criar ──────────────────────────────────────────────────────

  function abrirCriar() {
    setModoEditar(false);
    setEditandoId(null);
    setForm(CRECHE_CADASTRO_VAZIO);
    setErroCNPJ('');
    setAbrirModal(true);
  }

  // ─── Abrir modal editar ─────────────────────────────────────────────────────

  async function abrirEditar(creche: CrecheResumo) {
    setModoEditar(true);
    setEditandoId(creche.id);
    setErroCNPJ('');
    // Buscar dados completos pelo ID para ter todos os campos
    try {
      const res = await api.get<CrecheResumo>(`/creches/${creche.id}`);
      const c = res.data;
      const gestor = c.usuarios?.find(u => u.tipo_usuario === 'gestor') || c.usuarios?.[0];
      setForm({
        ...CRECHE_CADASTRO_VAZIO,
        cnpj: c.cnpj || '',
        razaoSocial: c.nome || '',
        nomeFantasia: c.nome_fantasia || '',
        telefone: c.telefone || '',
        responsavelEmail: gestor?.email || c.email || '',
        cep: c.cep || '',
        endereco: c.endereco || '',
        numero: c.numero || '',
        complemento: c.complemento || '',
        bairro: c.bairro || '',
        cidade: c.cidade || '',
        estado: c.estado || '',
        // Responsável Legal: a fonte é a creche (usada nos contratos); cadastros
        // antigos, feitos antes da unificação, caem para os dados do usuário-gestor.
        responsavelNome: c.representante_legal_nome || gestor?.nome || c.diretor || '',
        responsavelCpf: c.representante_legal_cpf || gestor?.cpf || '',
        responsavelRg: c.representante_legal_rg || gestor?.rg_numero || '',
        responsavelRgUf: c.representante_legal_rg_uf || gestor?.rg_uf || '',
        responsavelNacionalidade: c.representante_legal_nacionalidade || '',
        responsavelEstadoCivil: c.representante_legal_estado_civil || '',
        responsavelProfissao: c.representante_legal_profissao || '',
        plano: (c as any).plano || 'BRONZE',
      });
    } catch {
      // fallback: usar dados do resumo
      const gestor = creche.usuarios?.find(u => u.tipo_usuario === 'gestor') || creche.usuarios?.[0];
      setForm({
        ...CRECHE_CADASTRO_VAZIO,
        cnpj: creche.cnpj || '',
        razaoSocial: creche.nome || '',
        nomeFantasia: creche.nome_fantasia || '',
        telefone: creche.telefone || '',
        responsavelEmail: gestor?.email || creche.email || '',
        cep: creche.cep || '',
        endereco: creche.endereco || '',
        numero: creche.numero || '',
        complemento: creche.complemento || '',
        bairro: creche.bairro || '',
        cidade: creche.cidade || '',
        estado: creche.estado || '',
        responsavelNome: creche.representante_legal_nome || gestor?.nome || creche.diretor || '',
        responsavelCpf: creche.representante_legal_cpf || gestor?.cpf || '',
        responsavelRg: creche.representante_legal_rg || gestor?.rg_numero || '',
        responsavelRgUf: creche.representante_legal_rg_uf || gestor?.rg_uf || '',
        responsavelNacionalidade: creche.representante_legal_nacionalidade || '',
        responsavelEstadoCivil: creche.representante_legal_estado_civil || '',
        responsavelProfissao: creche.representante_legal_profissao || '',
        plano: creche.plano || 'BRONZE',
      });
    }
    setAbrirModal(true);
  }

  function fecharModal() {
    setAbrirModal(false);
    setModoEditar(false);
    setEditandoId(null);
    setForm(CRECHE_CADASTRO_VAZIO);
    setErroCNPJ('');
  }

  // ─── Busca CNPJ (BrasilAPI via proxy do backend) ─────────────────────────────

  async function buscarDadosCNPJ() {
    const resultado = await buscarCnpj(form.cnpj);
    if (!resultado) return;
    setForm(prev => ({
      ...prev,
      razaoSocial: resultado.razaoSocial || prev.razaoSocial,
      nomeFantasia: resultado.nomeFantasia || prev.nomeFantasia,
      endereco: resultado.endereco || prev.endereco,
      numero: resultado.numero || prev.numero,
      complemento: resultado.complemento || prev.complemento,
      bairro: resultado.bairro || prev.bairro,
      cidade: resultado.cidade || prev.cidade,
      estado: resultado.estado || prev.estado,
      cep: resultado.cep || prev.cep,
      telefone: resultado.telefone || prev.telefone,
      responsavelEmail: resultado.email || prev.responsavelEmail,
    }));
  }

  // ─── Busca CEP (ViaCEP) ─────────────────────────────────────────────────────

  async function buscarDadosCEP() {
    const resultado = await buscarCep(form.cep);
    if (!resultado) return;
    setForm(prev => ({
      ...prev,
      endereco: resultado.endereco || prev.endereco,
      bairro: resultado.bairro || prev.bairro,
      cidade: resultado.cidade || prev.cidade,
      estado: resultado.estado || prev.estado,
    }));
  }

  // ─── Salvar (criar ou editar) ───────────────────────────────────────────────

  async function salvarTenant() {
    if (!form.razaoSocial.trim() || !form.responsavelEmail.trim()) {
      const msg = 'Razão Social e E-mail são obrigatórios';
      showError(msg);
      return;
    }

    const cnpjLimpo   = form.cnpj.replace(/\D/g, '');
    const emailLower  = form.responsavelEmail.toLowerCase().trim();
    const nomeLower   = form.razaoSocial.toLowerCase().trim();

    // Verificação de duplicatas apenas ao CRIAR (edição nunca bloqueia)
    if (!modoEditar) {
      if (!form.responsavelNome.trim() || !form.responsavelCpf.trim() || !form.responsavelRg.trim() || !form.responsavelRgUf.trim()) {
        showError('Nome, CPF, RG e UF do responsável legal são obrigatórios ao criar um novo tenant.');
        return;
      }

      const cnpjDup = cnpjLimpo.length > 0
        ? creches.filter(c => String(c.cnpj || '').replace(/\D/g, '') === cnpjLimpo)
        : [];
      const emailDup = creches.filter(
        c => String(c.email || '').toLowerCase().trim() === emailLower
      );
      const nomeDup = creches.filter(
        c => String(c.nome || '').toLowerCase().trim() === nomeLower
      );

      if (cnpjDup.length > 0) {
        showError(`CNPJ já cadastrado! Creche existente: ${cnpjDup[0].nome} (${cnpjDup[0].status})`);
        return;
      }
      if (emailDup.length > 0) {
        showError(`E-mail já cadastrado! Creche existente: ${emailDup[0].nome} (${emailDup[0].status})`);
        return;
      }
      if (nomeDup.length > 0) {
        const ok = window.confirm(
          `Já existe uma creche com este nome:\n"${nomeDup[0].nome}" (${nomeDup[0].status})\n\nDeseja continuar mesmo assim?`
        );
        if (!ok) return;
      }
    }

    setSalvando(true);
    try {
      if (modoEditar && editandoId) {
        const payload = {
          nome:             form.razaoSocial.trim(),
          nome_fantasia:    form.nomeFantasia.trim() || null,
          cnpj:             cnpjLimpo || null,
          email:            emailLower,
          telefone:         form.telefone.trim() || null,
          cep:              form.cep.replace(/\D/g, '') || null,
          endereco:         form.endereco.trim() || null,
          numero:           form.numero.trim() || null,
          complemento:      form.complemento.trim() || null,
          bairro:           form.bairro.trim() || null,
          cidade:           form.cidade.trim() || null,
          estado:           form.estado || null,
          diretor:          form.responsavelNome.trim() || null,
          // Responsável Legal: mesmos dados alimentam o contrato (creche) e o
          // login do Gestor Master (usuário) — ver admin_* logo abaixo.
          representante_legal_nome:           form.responsavelNome.trim() || null,
          representante_legal_cpf:            form.responsavelCpf.trim() || null,
          representante_legal_rg:             form.responsavelRg.trim() || null,
          representante_legal_rg_uf:          form.responsavelRgUf.trim() || null,
          representante_legal_nacionalidade:  form.responsavelNacionalidade?.trim() || null,
          representante_legal_estado_civil:   form.responsavelEstadoCivil?.trim() || null,
          representante_legal_profissao:      form.responsavelProfissao?.trim() || null,
          admin_nome:       form.responsavelNome.trim() || undefined,
          admin_email:      emailLower || undefined,
          admin_cpf:        form.responsavelCpf.trim() || undefined,
          admin_rg:         form.responsavelRg.trim() || undefined,
          admin_rg_uf:      form.responsavelRgUf.trim() || undefined,
          plano:            form.plano || undefined,
        };
        await api.put(`/saas/tenants/${editandoId}`, payload);
        success(`Tenant "${form.razaoSocial}" atualizado com sucesso!`);
      } else {
        // Cadastro completo + usuário-gestor (recebe convite por e-mail para definir senha)
        await api.post('/saas/tenants', {
          cnpj: cnpjLimpo,
          creche_nome: form.razaoSocial.trim(),
          nome_fantasia: form.nomeFantasia.trim() || undefined,
          telefone: form.telefone.trim() || undefined,
          cep: form.cep.replace(/\D/g, '') || undefined,
          endereco: form.endereco.trim() || undefined,
          numero: form.numero.trim() || undefined,
          complemento: form.complemento.trim() || undefined,
          bairro: form.bairro.trim() || undefined,
          cidade: form.cidade.trim() || undefined,
          estado: form.estado || undefined,
          representante_legal_nome: form.responsavelNome.trim(),
          representante_legal_cpf: form.responsavelCpf.trim(),
          representante_legal_rg: form.responsavelRg.trim(),
          representante_legal_rg_uf: form.responsavelRgUf.trim(),
          representante_legal_nacionalidade: form.responsavelNacionalidade?.trim() || undefined,
          representante_legal_estado_civil: form.responsavelEstadoCivil?.trim() || undefined,
          representante_legal_profissao: form.responsavelProfissao?.trim() || undefined,
          admin_nome: form.responsavelNome.trim(),
          admin_email: emailLower,
          admin_cpf: form.responsavelCpf.trim(),
          admin_rg: form.responsavelRg.trim(),
          admin_rg_uf: form.responsavelRgUf.trim(),
          plano: form.plano,
        });
        success(`Tenant "${form.razaoSocial}" criado com sucesso! Convite enviado para ${emailLower}.`);
      }
      fecharModal();
      setFiltros({ status: '', plano: '', onboarding_status: '' });
      setSearchTerm('');
      await carregarDados();
    } catch (err: any) {
      const msg = err.response?.data?.message || (modoEditar ? 'Erro ao atualizar tenant' : 'Erro ao criar tenant');
      showError(msg);
    } finally {
      setSalvando(false);
    }
  }

  // ─── Filtros ────────────────────────────────────────────────────────────────

  let listagem = creches;
  if (filtros.status) {
    listagem = creches.filter(c => c.status === filtros.status);
  }
  listagem = listagem.filter(c => {
    const nome = (c.nome || c.nome_fantasia || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const s = searchTerm.toLowerCase();
    return nome.includes(s) || email.includes(s);
  });
  if (filtros.plano) listagem = listagem.filter(c => c.plano === filtros.plano);
  if (filtros.onboarding_status) listagem = listagem.filter(c => c.onboarding_status === filtros.onboarding_status);

  function fmtData(iso?: string) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR');
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4" />
          <p className="text-gray-600">Carregando tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-2xl p-8 text-white shadow-lg">
        <h1 className="text-4xl font-bold mb-2">Gestão de Tenants</h1>
        <p className="text-indigo-100">Gerenciamento centralizado de creches e solicitações de onboarding</p>
      </div>

      {/* Barra de busca + ações */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar creche por nome ou email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button onClick={() => setAbrirModalFiltros(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Filter size={18} /> Filtros
          </button>
          <button onClick={abrirCriar}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
            <Plus size={18} /> Novo Tenant
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {filtros.status
            ? `Tenants — ${filtros.status} (${listagem.length})`
            : `Todos os Tenants (${listagem.length})`}
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Creche</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Localização</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Plano</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Data</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {listagem.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    Nenhum tenant encontrado
                  </td>
                </tr>
              ) : (
                listagem.map(creche => (
                  <tr key={creche.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <strong className="text-gray-900 block">{creche.nome}</strong>
                      {creche.nome_fantasia && (
                        <span className="text-xs text-gray-500">{creche.nome_fantasia}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{creche.email || '—'}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {creche.cidade || '—'} / {creche.estado || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {creche.plano?.toUpperCase() || 'BRONZE'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const mapa: Record<string, { cor: string; texto: string }> = {
                          LEAD: { cor: 'bg-orange-100 text-orange-700', texto: 'Pendente Aprovação' },
                          TRIAL: { cor: 'bg-blue-100 text-blue-700', texto: 'Em Trial' },
                          PAGAMENTO_APROVADO: { cor: 'bg-teal-100 text-teal-700', texto: 'Pgto Aprovado' },
                          TENANT_PROVISIONADO: { cor: 'bg-teal-100 text-teal-700', texto: 'Provisionado' },
                          ATIVO: { cor: 'bg-green-100 text-green-700', texto: 'Assinante Efetivo' },
                          STANDBY: { cor: 'bg-gray-200 text-gray-700', texto: 'Stand by (Comercial)' },
                        };
                        const s = mapa[creche.onboarding_status] || { cor: 'bg-gray-100 text-gray-600', texto: creche.onboarding_status };
                        return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.cor}`}>{s.texto}</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{fmtData(creche.criado_em)}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {/* Editar */}
                        <button
                          className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 flex items-center gap-1"
                          title="Editar dados do tenant"
                          onClick={() => abrirEditar(creche)}
                        >
                          <Pencil size={11} /> Editar
                        </button>

                        {/* Aprovar (lead pendente — inicia o trial de 7 dias) */}
                        {creche.onboarding_status === 'LEAD' && (
                          <button
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            onClick={async () => {
                              try {
                                await api.post(`/saas/provisionar-creche/${creche.id}`);
                                success(`Tenant "${creche.nome}" aprovado! Trial de 7 dias iniciado.`);
                                await carregarDados();
                              } catch (err: any) {
                                showError(err.response?.data?.message || 'Erro ao aprovar tenant.');
                              }
                            }}
                          >
                            ✅ Aprovar
                          </button>
                        )}

                        {/* Provisionar (assinatura paga aprovada — sem trial) */}
                        {creche.onboarding_status === 'PAGAMENTO_APROVADO' && (
                          <button
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            onClick={async () => {
                              try {
                                await api.post(`/saas/provisionar-creche/${creche.id}`);
                                await carregarDados();
                              } catch { alert('Erro ao provisionar tenant.'); }
                            }}
                          >
                            Provisionar
                          </button>
                        )}

                        {/* Ativar assinatura efetiva (encerra Trial/stand by → passa a faturar) */}
                        {(creche.onboarding_status === 'TRIAL' || creche.onboarding_status === 'STANDBY') && (
                          <button
                            className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
                            title="Converter em assinante efetivo (a partir daqui o sistema pode faturar)"
                            onClick={async () => {
                              if (!window.confirm(`Confirmar "${creche.nome}" como ASSINANTE EFETIVO? A partir daqui o faturamento fica liberado.`)) return;
                              try {
                                await api.post(`/saas/tenants/${creche.id}/ativar-assinatura`);
                                success(`"${creche.nome}" agora é assinante efetivo.`);
                                await carregarDados();
                              } catch (err: any) {
                                showError(err.response?.data?.message || 'Erro ao ativar assinatura.');
                              }
                            }}
                          >
                            ✔ Ativar assinatura
                          </button>
                        )}

                        {/* Mover para stand by (bloqueia acesso, sem faturar) */}
                        {creche.onboarding_status === 'TRIAL' && (
                          <button
                            className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                            title="Bloquear acesso e mover para stand by (aguardando Comercial)"
                            onClick={async () => {
                              if (!window.confirm(`Mover "${creche.nome}" para STAND BY? O acesso será bloqueado e nenhuma cobrança será gerada.`)) return;
                              try {
                                await api.post(`/saas/tenants/${creche.id}/standby`, { motivo: 'Movido manualmente pelo gestor do SaaS' });
                                success(`"${creche.nome}" movido para stand by (acesso bloqueado).`);
                                await carregarDados();
                              } catch (err: any) {
                                showError(err.response?.data?.message || 'Erro ao mover para stand by.');
                              }
                            }}
                          >
                            ⏸ Stand by
                          </button>
                        )}

                        {/* Negar */}
                        <button
                          className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          onClick={() => setDialogId(creche.id)}
                        >
                          Negar
                        </button>

                        {/* Excluir */}
                        <button
                          className="px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-900 flex items-center gap-1"
                          title="Excluir permanentemente"
                          onClick={() => { setDeleteConfirmId(creche.id); setDeleteConfirmNome(creche.nome); }}
                        >
                          <Trash2 size={11} /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal Criar / Editar ─────────────────────────────────────────────── */}
      {abrirModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-6"
          onClick={fecharModal}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">
                {modoEditar ? '✏️ Cadastro Creche/Tenant' : '➕ Cadastro Creche/Tenant'}
              </h3>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            <CrecheCadastroFields
              value={form}
              onChange={setForm}
              mostrarDocumentos
              mostrarAvisoConvite={!modoEditar}
              onBuscarCnpj={buscarDadosCNPJ}
              buscandoCnpj={buscandoCNPJ}
              erroCnpj={erroCNPJ}
              onBuscarCep={buscarDadosCEP}
              buscandoCep={buscandoCEP}
              disabled={salvando}
            />

            <div className="flex gap-3 mt-6">
              <button onClick={fecharModal}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">
                Cancelar
              </button>
              <button onClick={salvarTenant} disabled={salvando}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-60 flex items-center justify-center gap-2">
                {salvando
                  ? <><span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Salvando...</>
                  : modoEditar ? '💾 Salvar Alterações' : '✅ Criar Tenant'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Filtros ─────────────────────────────────────────────────────── */}
      {abrirModalFiltros && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setAbrirModalFiltros(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">🔍 Filtros Avançados</h3>
              <button onClick={() => setAbrirModalFiltros(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 mb-6">
              {[
                { label: 'Status', key: 'status', opts: [
                  ['', 'Todos'], ['pendente', 'Pendente'], ['ativa', 'Ativa'],
                  ['suspensa', 'Suspensa'], ['negada', 'Negada'],
                ]},
                { label: 'Plano', key: 'plano', opts: [
                  ['', 'Todos'], ['BRONZE', 'Bronze'], ['PRATA', 'Prata'], ['OURO', 'Ouro'],
                ]},
                { label: 'Situação Onboarding', key: 'onboarding_status', opts: [
                  ['', 'Todos'], ['LEAD', 'Lead'], ['PAGAMENTO_APROVADO', 'Pgto Aprovado'],
                  ['TRIAL', 'Em Trial'], ['TENANT_PROVISIONADO', 'Provisionado'], ['ATIVO', 'Assinante Efetivo'],
                  ['STANDBY', 'Stand by (Comercial)'],
                ]},
              ].map(({ label, key, opts }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select
                    value={(filtros as any)[key]}
                    onChange={e => setFiltros(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setFiltros({ status:'', plano:'', onboarding_status:'' }); setAbrirModalFiltros(false); }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                Limpar
              </button>
              <button onClick={() => setAbrirModalFiltros(false)}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Confirmação de Exclusão ────────────────────────────────────── */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
          onClick={() => setDeleteConfirmId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Excluir Tenant</h3>
                <p className="text-sm text-gray-500">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <p className="text-sm text-red-800 font-medium">Você está prestes a excluir:</p>
              <p className="text-base font-bold text-red-900 mt-1">{deleteConfirmNome}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setDeleteConfirmId(null); setDeleteConfirmNome(''); }}
                disabled={excluindo}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">
                Cancelar
              </button>
              <button onClick={() => excluirTenant(deleteConfirmId)} disabled={excluindo}
                className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {excluindo
                  ? <><span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Excluindo...</>
                  : <><Trash2 size={15} /> Confirmar Exclusão</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dialog Negar ─────────────────────────────────────────────────────── */}
      {dialogId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setDialogId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
            onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">❌ Negar Solicitação</h3>
            <p className="text-gray-600 mb-3 text-sm">Informe o motivo da negação:</p>
            <textarea
              placeholder="Ex: Documentação inválida, plano não compatível..."
              value={motivoNegacao}
              onChange={e => setMotivoNegacao(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => setDialogId(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancelar
              </button>
              <button onClick={() => atualizarStatus(dialogId, 'negada', motivoNegacao)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Confirmar Negação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
